import { FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { isLessThan36Months } from '@/checkout/templates/Checkout/Forms/CheckoutForm.helpers';
import {
  buyTypes,
  checkIsValidAbn,
  employmentStatusOptions,
  financeQuoteInfoRadioTypes,
  repaymentFrequencyOptions,
  residencyStatusOptions,
  residentialStatusOptions,
} from '@/checkout/templates/Checkout/Forms/CheckoutForm.types';
import { MAX_INT } from '@/constants/constants';
import { regexAbn, regexMobilePhone } from '@/constants/regex.constants';
import {
  FINANCE_FAMILY_STATUS,
  FINANCE_RESIDENCY_STATUS,
  FINANCE_RESIDENTIAL_STATUS,
  FinanceQuote,
  RESOLUTION_OF_STATUS_VISA_SUBCLASS,
} from '@/data/FinanceApi/FinanceApi.types';
import { MICROCOPY } from '@/data/microcopy/microcopyDictionary';
import { getInputAddressAutocompleteValidation } from '@/general/components/forms/FormikInputAddressAutocomplete/FormikInputAddressAutocomplete';
import {
  isAfter1900,
  isOver18YearsOld,
  isUnder120YearsOld,
  isValidDate,
  isValidDateInTheFuture,
  isValidPartialDateBefore,
  isValidPartialDateInThePast,
} from '@/utils/date';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';

//
// Loan details form: Build year < 15 years from current year
//
const buildYearArray = [...Array(15).keys()].map((_, index) => new Date().getFullYear() - index);
export const buildYears = buildYearArray.map((item) => ({
  label: item.toString(),
  value: item.toString(),
}));

//
// Family and Residential form
//
export const familyStatusOptions = [
  {
    value: FINANCE_FAMILY_STATUS.SINGLE,
    label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_FAMILY_STATUS_OPTION_SINGLE,
  },
  {
    value: FINANCE_FAMILY_STATUS.MARRIED,
    label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_FAMILY_STATUS_OPTION_MARRIED,
  },
  {
    value: FINANCE_FAMILY_STATUS.DE_FACTO,
    label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_FAMILY_STATUS_OPTION_DE_FACTO,
  },
  {
    value: FINANCE_FAMILY_STATUS.WIDOWED,
    label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_FAMILY_STATUS_OPTION_WIDOWED,
  },
  {
    value: FINANCE_FAMILY_STATUS.SEPARATED,
    label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_FAMILY_STATUS_OPTION_SEPARATED,
  },
  {
    value: FINANCE_FAMILY_STATUS.DIVORCED,
    label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_FAMILY_STATUS_OPTION_DIVORCED,
  },
  {
    value: FINANCE_FAMILY_STATUS.OTHER,
    label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_FAMILY_STATUS_OPTION_OTHER,
  },
] as const;

export const enum NEWS_AND_MARKETING {
  CHECKED = 'checked',
}

export const isCheckNewsAndMarketingOptions = [
  { value: NEWS_AND_MARKETING.CHECKED, label: MICROCOPY.PERSONALIZED_QUOTE_SIGNUP_CHECKBOX },
];

export const enum TERMS_AND_CONDITIONS {
  CHECKED = 'checked',
}

export const isCheckTermsAndConditionsOptions = [
  { value: TERMS_AND_CONDITIONS.CHECKED, label: MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_TERM_CONDITIONS_LABEL },
];
/**
 * Full personalised quote payload
 */
type PersonalisedQuotePayload = Prettify<
  {
    loanType: LoanTypeFormValues;
  } & {
    isBusinessUseSelected: boolean;
  } & {
    loanDetails: LoanDetailsFormValues;
  } & {
    email: EmailFormValues;
  } & {
    signIn: SigninFormValues;
  } & {
    phone: PhoneNumberFormValues;
  } & {
    personalDetails: PersonalDetailsFormValues;
  } & {
    accountSignupDetails: SignupFormValues & { verificationId: string };
  } & {
    accountSignupVerification: VerificationTokenFormValues;
  } & {
    familyDetails: FamilyResidentialFormValues;
  } & {
    employmentAndCitizenshipDetails: EmploymentCitizenshipFormValues;
  } & {
    termsAndConditionsDetails: TermsAndConditionsFormValues;
  } & {
    financeQuoteRequest: PersonalisedQuotePayloadPaymentOptionFinanceQuoteRequest;
    financeQuote: PersonalisedQuotePayloadPaymentOptionFinanceQuote;
  } & {
    isQuoteConfirmed: boolean;
  }
>;

/**
 * Helper type
 * All properties in personalised quote payload without unions
 */
type PersonalisedQuotePayloadAll = Prettify<
  Omit<UnionToIntersection<PersonalisedQuotePayload>, 'loanType'> & { loanType: LoanTypeFormValues }
>;

/**
 * Partial personalised quote payload
 */
export type PersonalisedQuotePayloadPartial = Prettify<Partial<PersonalisedQuotePayloadAll>>;

export type PersonalizedQuoteFormValues =
  | LoanDetailsFormValues
  | EmailFormValues
  | PhoneNumberFormValues
  | LoanTypeFormValues
  | SigninFormValues
  | ResetPasswordFormValues
  | ResendPasswordFormValues
  | PersonalDetailsFormValues
  | FamilyResidentialFormValues
  | EmploymentCitizenshipFormValues
  | TermsAndConditionsFormValues
  | PersonalisedFinanceQuotesFormValues
  | SignupFormValues
  | VerificationTokenFormValues
  | ConfirmationFormValues;

export interface PersonalizedQuoteFormProps<T extends PersonalizedQuoteFormValues> {
  values: Partial<T>;
  /**
   * Handle form submission
   * @returns true if successful, string with error message otherwise
   */
  onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => Promise<true | string>;
  onSubmitTracking?: (success: boolean, reasons?: string[]) => void;
  onBack?: () => void;
  setValuesPartial?: (values: PersonalisedQuotePayloadPartial, updateFinanceInformation?: boolean) => void;
  hideTitle?: boolean;
  fetchCarsCount?: (buildYear: string, purchasePrice: string) => Promise<number>;
  onClickResetPassword?: () => void;
  onResendTokenVerificationCode?: () => Promise<void>;
  email?: string;
  phone?: string;
  isLoggedIn?: boolean;
}

//
//  Loan details
//
export const LoanDetailsFormValidation = Yup.object({
  estimatedPurchasePrice: Yup.number()
    .required('Please specify your purchase price')
    .max(MAX_INT, `Purchase price is too large`),
  depositAmount: Yup.number()
    .optional()
    .test('deposit-not-exceed-purchase-price', 'Deposit cannot exceed the purchase price', function (value) {
      const { estimatedPurchasePrice } = this.parent;
      if (!value || !estimatedPurchasePrice) return true;
      return value <= estimatedPurchasePrice;
    })
    .test('minimum-loan-amount', 'Loan amount must be at least $5,000', function (value) {
      const { estimatedPurchasePrice } = this.parent;
      if (!value || !estimatedPurchasePrice) return true;
      return estimatedPurchasePrice - value >= 5000;
    }),
  preferredBuildYear: Yup.string()
    .required('Please specify your preferred build year')
    .oneOf(buildYears.map((item) => item.value)),
});
export type LoanDetailsFormValues = ReturnType<typeof LoanDetailsFormValidation.validateSync>;

//
// Loan type
//
export const LoanTypeFormValidation = Yup.object({
  loanType: Yup.string()
    .oneOf(buyTypes.map((item) => item.value))
    .required(),
});
export type LoanTypeFormValues = ReturnType<typeof LoanTypeFormValidation.validateSync>;

//
// Email form
//
export const EmailFormValidation = Yup.object({
  email: Yup.string().email('Please enter a valid email address').required('Please enter a valid email address'),
});

export type EmailFormValues = ReturnType<typeof EmailFormValidation.validateSync>;

//
// Phone form
//
const phoneError = 'Please enter a valid Australian mobile number';

const PhoneNumberFormValidation = Yup.object({
  phone: Yup.string().required(phoneError).matches(regexMobilePhone, phoneError),
});

type PhoneNumberFormValues = ReturnType<typeof PhoneNumberFormValidation.validateSync>;

//
// Sign in form
//
export const SigninFormValidation = Yup.object({
  password: Yup.string().required('Password incorrect, please try again'),
});

export type SigninFormValues = ReturnType<typeof SigninFormValidation.validateSync>;

//
// Reset password form
//
const ResetPasswordFormValidation = Yup.object({
  email: Yup.string().email().required('Please enter a valid email address'),
});

export type ResetPasswordFormValues = ReturnType<typeof ResetPasswordFormValidation.validateSync>;

//
// Resend password form
//
const ResendPasswordFormValidation = Yup.object({});

export type ResendPasswordFormValues = ReturnType<typeof ResendPasswordFormValidation.validateSync>;

//
// Personal details form
//
export const PersonalDetailsFormValidation = Yup.object({
  firstName: Yup.string().required('Please specify your first name'),
  middleName: Yup.string().optional(),
  lastName: Yup.string().required('Please specify your last name'),
  phone: Yup.string()
    .required(phoneError)
    .test('phone', phoneError, (phone: string) => regexMobilePhone.test(formatPhoneNumber(phone))),
  dateOfBirth: Yup.string()
    .required('Please specify your date of birth')
    .test('birthDay', 'Invalid date of birth', (value: string) => isValidDate(value))
    .test('birthDay', 'You need to be over 18 years to purchase with us', (value: string) => isOver18YearsOld(value))
    .test('birthDay', 'That seems vintage! Please check your date of birth', (value: string) =>
      isUnder120YearsOld(value),
    ),
  licenceNumber: Yup.string()
    .optional()
    .test(
      'len',
      'Must be a valid licence number with a minimum of 6 and maximum of 11 alphanumeric characters',
      (val) => !val?.length || (val?.length >= 6 && val?.length <= 11 && /^[a-zA-Z0-9]+$/.test(val)),
    ),
  isBusinessUseSelected: Yup.boolean(),
  abn: Yup.string().when('isBusinessUseSelected', {
    is: true,
    then: Yup.string()
      .matches(regexAbn, 'Please specify a valid ABN')
      .required(
        'Please specify your business ABN. Quickly find your ABN by visiting the [Australian Business Register (ABR)](https://abr.business.gov.au/)',
      )
      .test('check-valid-abn', 'Please specify a valid ABN', checkIsValidAbn),
    otherwise: Yup.string().optional(),
  }),
});

export type PersonalDetailsFormValues = ReturnType<typeof PersonalDetailsFormValidation.validateSync>;

//
//  Family and Residential
//
export const FamilyResidentialFormValidation = Yup.object({
  familyStatus: Yup.string().when('isBusinessUseSelected', {
    is: false,
    then: Yup.string()
      .required('Please select your marital status')
      .oneOf(familyStatusOptions.map((item) => item.value)),
    otherwise: Yup.string().optional(),
  }),
  address: getInputAddressAutocompleteValidation(),
  residentialStatus: Yup.string()
    .oneOf(residentialStatusOptions.map((item) => item.value))
    .required('Please select your residential status'),
  currentAddressStartDate: Yup.string()
    .required('Please provide your current address move in date')
    .test('moveInDate', 'You must enter a valid date in the past', (value: string) =>
      isValidPartialDateInThePast(value),
    )
    .test('isAfter1900', 'You must enter a date after 01/1900', (value: string) => isAfter1900(value)),
  hasTenancyAgreement: Yup.string().when(['isBusinessUseSelected', 'residentialStatus'], {
    is: (isBusinessUseSelected: boolean, residentialStatus: FINANCE_RESIDENTIAL_STATUS) =>
      !isBusinessUseSelected &&
      [FINANCE_RESIDENTIAL_STATUS.RENTING, FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION].includes(
        residentialStatus,
      ),
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required(`Please select if you're on the tenancy agreement`),
  }),
  hasMortgageOnCurrentAddress: Yup.string().when('residentialStatus', {
    is: (residentialStatus: string) => ([FINANCE_RESIDENTIAL_STATUS.HOMEOWNER] as string[]).includes(residentialStatus),
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required('Please select if you also own a property with a mortgage'),
  }),
  hasInvestmentProperty: Yup.string().when(['residentialStatus'], {
    is: (residentialStatus: FINANCE_RESIDENTIAL_STATUS) =>
      [
        FINANCE_RESIDENTIAL_STATUS.RENTING,
        FINANCE_RESIDENTIAL_STATUS.BOARDER,
        FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
        FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
      ].includes(residentialStatus),
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required('Please select if you own an investment property'),
  }),
  hasMortgageOnInvestmentProperty: Yup.string().when(['residentialStatus', 'hasInvestmentProperty'], {
    is: (residentialStatus: FINANCE_RESIDENTIAL_STATUS, hasInvestmentProperty: FINANCE_QUOTE_RADIO_TYPE) =>
      [
        FINANCE_RESIDENTIAL_STATUS.RENTING,
        FINANCE_RESIDENTIAL_STATUS.BOARDER,
        FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
        FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
      ].includes(residentialStatus) && hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
    then: Yup.string()
      .oneOf(financeQuoteInfoRadioTypes.map((item) => item.value))
      .required('Please select if you have mortgage on an investment property'),
  }),
  investmentPropertyAddress: Yup.object().when(
    ['isBusinessUseSelected', 'residentialStatus', 'hasInvestmentProperty', 'hasMortgageOnInvestmentProperty'],
    {
      is: (
        isBusinessUseSelected: boolean,
        residentialStatus: FINANCE_RESIDENTIAL_STATUS,
        hasInvestmentProperty: FINANCE_QUOTE_RADIO_TYPE,
        hasMortgageOnInvestmentProperty: FINANCE_QUOTE_RADIO_TYPE,
      ) =>
        !isBusinessUseSelected &&
        [FINANCE_RESIDENTIAL_STATUS.RENTING, FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION].includes(
          residentialStatus,
        ) &&
        hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION &&
        hasMortgageOnInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
      then: getInputAddressAutocompleteValidation(),
    },
  ),
  previousResidentialAddress: Yup.object().when('currentAddressStartDate', {
    is: (currentAddressStartDate: string) => isLessThan36Months(currentAddressStartDate),
    then: getInputAddressAutocompleteValidation(),
  }),
  previousAddressStartDate: Yup.string().when('currentAddressStartDate', (currentAddressStartDate: string) => {
    if (isLessThan36Months(currentAddressStartDate)) {
      return Yup.string()
        .required('Please provide your previous address move in date')
        .test('moveInDate', 'You must enter a valid date before your current address move in date', (value: string) =>
          isValidPartialDateBefore(value, currentAddressStartDate),
        );
    }
    return Yup.string();
  }),
  isBusinessUseSelected: Yup.boolean().default(false).optional(),
});
export type FamilyResidentialFormValues = ReturnType<typeof FamilyResidentialFormValidation.validateSync>;

//
//  Employment and Citizenship form
//
export const EmploymentCitizenshipFormValidation = Yup.object({
  employmentStatus: Yup.string().when('isBusinessUseSelected', {
    is: false,
    then: Yup.string()
      .required('Please specify your employment status')
      .oneOf(employmentStatusOptions.map((item) => item.value)),
    otherwise: Yup.string().optional(),
  }),
  startedEmployerDate: Yup.date()
    .typeError('Please specify your starting date')
    .when('isBusinessUseSelected', {
      is: false,
      then: Yup.date().typeError('Please specify your starting date').required('Please specify your starting date'),
      otherwise: Yup.date().optional(),
    }),
  jobTitle: Yup.string().when('isBusinessUseSelected', {
    is: false,
    then: Yup.string().required('Please specify your job title'),
    otherwise: Yup.string().optional(),
  }),
  totalYearlyIncomeBeforeTax: Yup.number()
    .typeError('Please specify your total yearly income before tax')
    .when('isBusinessUseSelected', {
      is: false,
      then: Yup.number().required('Please specify your total yearly income before tax'),
      otherwise: Yup.number().optional(),
    }),
  residencyStatus: Yup.string()
    .required('Please specify your residential status')
    .oneOf(residencyStatusOptions.map((item) => item.value)),
  visaSubclassNumber: Yup.number().when('residencyStatus', {
    is: (residencyStatus: FINANCE_RESIDENCY_STATUS) => residencyStatus === FINANCE_RESIDENCY_STATUS.TEMPORARY_RESIDENT,
    then: Yup.number()
      .typeError('Visa subclass number must be number type')
      .required('Please enter your visa subclass number')
      .integer('Visa subclass number must be 3 digits')
      .min(100, 'Visa subclass number must be 3 digits')
      .max(999, 'Visa subclass number must be 3 digits'),
  }),
  visaExpiryDate: Yup.date()
    .typeError('You must enter a valid date in the future')
    .when(['residencyStatus', 'visaSubclassNumber'], {
      is: (residencyStatus: FINANCE_RESIDENCY_STATUS, visaSubclassNumber: number) =>
        visaSubclassNumber !== undefined &&
        residencyStatus === FINANCE_RESIDENCY_STATUS.TEMPORARY_RESIDENT &&
        visaSubclassNumber.toString().trim() !== RESOLUTION_OF_STATUS_VISA_SUBCLASS,
      then: Yup.date()
        .typeError('You must enter a valid date in the future')
        .required('Please provide your visa expiry date')
        .test('visaExpiryDate', 'You must enter a valid date in the future', (value: string) =>
          isValidDateInTheFuture(value),
        ),
      otherwise: Yup.date().typeError('You must enter a valid date in the future'),
    }),
  isBusinessUseSelected: Yup.boolean().default(false).optional(),
});
export type EmploymentCitizenshipFormValues = ReturnType<typeof EmploymentCitizenshipFormValidation.validateSync>;

//
// Terms and conditions
//
export const TermsAndConditionsFormValidation = Yup.object({
  isBusinessUseSelected: Yup.boolean().default(false).optional(),
  isCheckTermsAndConditions: Yup.string().required('Please accept the terms and conditions to proceed'),
});

export type TermsAndConditionsFormValues = ReturnType<typeof TermsAndConditionsFormValidation.validateSync>;

//
// Finance quote info
//
export const enum FINANCE_QUOTE_RADIO_TYPE {
  YES_OPTION = 'yes',
}

type PersonalisedQuotePayloadPaymentOptionFinanceQuoteRequest = {
  quoteRequestId: string;
};
export type PersonalisedQuotePayloadPaymentOptionFinanceQuote = {
  quoteId: string[] | null;
  quoteInfo?: (FinanceQuote | undefined)[];
};

//
// Signup form
//
export const SignupFormValidation = Yup.object({
  newPassword: Yup.string().required('A password is required'),
  newsAndMarketing: Yup.string().optional(),
});

export type SignupFormValues = ReturnType<typeof SignupFormValidation.validateSync>;

//
// Token verification form
//
export const VerificationTokenFormValidation = Yup.object({
  verificationCode: Yup.string()
    .min(4, 'The verification code must be 4 digits')
    .max(4, 'The verification code must be 4 digits')
    .required('A verification code is required'),
  isRequestingNewCode: Yup.boolean().oneOf([false]).required(),
});

export type VerificationTokenFormValues = ReturnType<typeof VerificationTokenFormValidation.validateSync>;

export type ConfirmationFormValues = {
  isQuoteConfirmed: boolean;
};

export const FinanceQuotesFormValidation = Yup.object({
  quoteRequestId: Yup.string().required(),
  quoteId: Yup.array().nullable().of(Yup.string()),
  isVisaHolder: Yup.boolean().required(),
  buyType: Yup.string()
    .oneOf(buyTypes.map((item) => item.value))
    .required(),
  repaymentFrequency: Yup.string()
    .oneOf(repaymentFrequencyOptions.map((item) => item.value))
    .required(),
  loanTerm: Yup.number().required(),
  carPrice: Yup.number().required(),
  deposit: Yup.number().required(),
  quoteInfo: Yup.array().of(Yup.object<FinanceQuote>().notRequired()),
});
export type PersonalisedFinanceQuotesFormValues = ReturnType<typeof FinanceQuotesFormValidation.validateSync>;
